import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip as RechartsTooltip, ResponsiveContainer
} from 'recharts';
import { ArrowUpRight, ArrowDownRight, RefreshCw, Info, Download, DatabaseZap } from 'lucide-react';

// MAPPING: Backend Keys -> Manager Friendly Labels & Tooltips
const METRIC_DEFINITIONS = {
    "delivered": { label: "Delivered", desc: "Emails that were accepted by the recipient's server." },
    "opens": { label: "Opens", desc: "Total number of times emails were opened." },
    "clicks": { label: "Clicks", desc: "Total number of times links in emails were clicked." },
    "bounces": { label: "Bounces", desc: "Emails that bounced back undelivered." },
    "dropped": { label: "Dropped", desc: "Emails that were dropped and not sent." },
    "default": { label: "Metric", desc: "" }
};

const DashboardLoader = () => (
    <div className="space-y-6 animate-pulse">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {[1, 2, 3].map(i => <div key={i} className="h-32 bg-gray-200 rounded-lg"></div>)}
        </div>
        {/* Single Chart Placeholder */}
        <div className="h-80 bg-gray-200 rounded-lg"></div>
        <div className="h-64 bg-gray-200 rounded-lg"></div>
    </div>
);

const KPICard = ({ label, value, subtext, trend, color = "blue" }) => {
    const colors = {
        blue: "text-blue-600 bg-blue-50 border-blue-100",
        green: "text-green-600 bg-green-50 border-green-100",
        purple: "text-purple-600 bg-purple-50 border-purple-100",
        orange: "text-orange-600 bg-orange-50 border-orange-100"
    };

    return (
        <div className="bg-white p-6 rounded-xl border border-gray-100 shadow-sm flex flex-col justify-between">
            <div className="flex justify-between items-start">
                <p className="text-sm font-medium text-gray-500">{label}</p>
                <span className={`p-1.5 rounded-md ${colors[color]}`}>
                    {trend === 'up' ? <ArrowUpRight className="h-4 w-4" /> : <ArrowDownRight className="h-4 w-4" />}
                </span>
            </div>
            <div className="mt-4">
                <h3 className="text-3xl font-bold text-gray-900">{value}</h3>
                <p className="text-xs text-gray-400 mt-1">{subtext}</p>
            </div>
        </div>
    );
};

export default function EmailDashboard() {
    const navigate = useNavigate();
    const [loading, setLoading] = useState(true);
    const [metrics, setMetrics] = useState({});
    const [errorMessage, setErrorMessage] = useState('');
    const abortControllerRef = useRef(null);
    const debounceTimerRef = useRef(null);

    const [dates, setDates] = useState(() => {
        const today = new Date();
        const past = new Date();
        past.setDate(today.getDate() - 7);

        return {
            start: past.toISOString().split('T')[0],
            end: today.toISOString().split('T')[0]
        };
    });

    // Validate that start <= end; if not, auto-correct
    const handleDateChange = (field, value) => {
        setDates(prev => {
            const next = { ...prev, [field]: value };

            if (next.start > next.end) {
                // If user sets start after end (or end before start), clamp the other
                if (field === 'start') next.end = value;
                else next.start = value;
            }

            return next;
        });
    };

    const fetchData = useCallback(async (signal) => {
        setLoading(true);
        setErrorMessage('');

        try {
            const endpoint = process.env.REACT_APP_METRICS_ENDPOINT;
            const apiKey = process.env.REACT_APP_X_API_KEY;

            if (!endpoint) {
                setErrorMessage(
                    'Unable to load metrics — the service is not configured. Please contact your administrator.'
                );
                setLoading(false);
                return;
            }

            const params = new URLSearchParams();
            params.append('start_date', dates.start);
            params.append('end_date', dates.end);

            const headers = {
                'Content-Type': 'application/json',
            };

            if (apiKey) {
                headers['X-API-KEY'] = apiKey;
            }

            const response = await fetch(`${endpoint}?${params.toString()}`, {
                method: 'GET',
                headers: headers,
                signal,
            });

            if (!response.ok) {
                throw new Error(`API error: ${response.status}`);
            }

            const data = await response.json();

            if (data && data.success && data.data) {
                const apiMetrics = data.data.metrics || [];

                const getMetric = (label) =>
                    apiMetrics.find(metric => metric.label === label)?.value || 0;

                const normalized = {
                    delivered: getMetric('Delivered'),
                    opens: getMetric('Opened'),
                    clicks: getMetric('Clicked'),
                    bounces: getMetric('Bounced'),
                    dropped: getMetric('Dropped'),
                };

                setMetrics(normalized);
            } else {
                setErrorMessage(
                    'We received an unexpected response. Please try again or contact support if the issue persists.'
                );
            }
        } catch (error) {
            if (error.name === 'AbortError') return;

            const message = error?.message || '';
            const isNetworkOrCors =
                message === 'Failed to fetch' ||
                message.toLowerCase().includes('cors');

            if (isNetworkOrCors) {
                setErrorMessage(
                    'Unable to connect to the metrics service. Please check your connection and try again.'
                );
            } else {
                setErrorMessage(
                    'Something went wrong while loading metrics. Please try again or contact support if the issue persists.'
                );
            }
        } finally {
            setLoading(false);
        }
    }, [dates]);

    // Debounced fetch: waits 600ms after the last date change before firing
    useEffect(() => {
        // Cancel any pending debounce timer
        if (debounceTimerRef.current) {
            clearTimeout(debounceTimerRef.current);
        }

        debounceTimerRef.current = setTimeout(() => {
            // Abort any in-flight request
            if (abortControllerRef.current) {
                abortControllerRef.current.abort();
            }

            const controller = new AbortController();
            abortControllerRef.current = controller;
            fetchData(controller.signal);
        }, 600);

        return () => {
            if (debounceTimerRef.current) {
                clearTimeout(debounceTimerRef.current);
            }
        };
    }, [fetchData]);

    // Manual refresh bypasses debounce
    const handleRefresh = () => {
        if (debounceTimerRef.current) {
            clearTimeout(debounceTimerRef.current);
        }

        if (abortControllerRef.current) {
            abortControllerRef.current.abort();
        }

        const controller = new AbortController();
        abortControllerRef.current = controller;
        fetchData(controller.signal);
    };

    // Export current metrics as CSV
    const exportCSV = () => {
        const entries = Object.entries(metrics);

        if (entries.length === 0) return;

        const rows = [
            ['Metric', 'Label', 'Value'],
            ...entries.map(([key, value]) => {
                const def =
                    METRIC_DEFINITIONS[key] ||
                    METRIC_DEFINITIONS.default;

                return [key, def.label, value];
            }),
            [],
            ['KPI', 'Value'],
            [
                'Open Rate',
                metrics.delivered
                    ? ((metrics.opens / metrics.delivered) * 100).toFixed(1) + '%'
                    : '-'
            ],
            [
                'Click Through Rate',
                metrics.delivered
                    ? ((metrics.clicks / metrics.delivered) * 100).toFixed(1) + '%'
                    : '-'
            ],
            [
                'Bounce Rate',
                metrics.delivered
                    ? ((metrics.bounces / metrics.delivered) * 100).toFixed(1) + '%'
                    : '-'
            ],
            [],
            ['Period', `${dates.start} to ${dates.end}`],
            ['Exported', new Date().toISOString()],
        ];

        const csv = rows
            .map(r => r.map(v => `"${v}"`).join(','))
            .join('\n');

        const blob = new Blob(
            [csv],
            { type: 'text/csv;charset=utf-8;' }
        );

        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');

        a.href = url;
        a.download = `email-metrics_${dates.start}_${dates.end}.csv`;
        a.click();

        URL.revokeObjectURL(url);
    };

    // Derived KPIs
    const openRate = metrics.delivered
        ? ((metrics.opens / metrics.delivered) * 100).toFixed(1) + '%'
        : '-';

    const clickRate = metrics.delivered
        ? ((metrics.clicks / metrics.delivered) * 100).toFixed(1) + '%'
        : '-';

    const bounceRate = metrics.delivered
        ? ((metrics.bounces / metrics.delivered) * 100).toFixed(1) + '%'
        : '-';

    const funnelData = [
        { name: 'Delivered', value: metrics.delivered || 0 },
        { name: 'Opened', value: metrics.opens || 0 },
        { name: 'Clicked', value: metrics.clicks || 0 },
    ];

    return (
        <div className="space-y-8">

            {/* Header */}
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                <div>
                    <h2 className="text-2xl font-bold text-gray-900">
                        Insync Email Service (IES) Health
                    </h2>

                    <p className="text-sm text-gray-500">
                        Transactional performance overview for the Insync Email Service.
                    </p>
                </div>

                <div className="flex items-center gap-3 bg-white p-1.5 rounded-lg border shadow-sm">
                    <input
                        type="date"
                        value={dates.start}
                        max={new Date().toISOString().split('T')[0]}
                        className="text-sm border-none focus:ring-0 text-gray-600 bg-transparent"
                        onChange={e =>
                            handleDateChange('start', e.target.value)
                        }
                    />

                    <span className="text-gray-300">to</span>

                    <input
                        type="date"
                        value={dates.end}
                        max={new Date().toISOString().split('T')[0]}
                        className="text-sm border-none focus:ring-0 text-gray-600 bg-transparent"
                        onChange={e =>
                            handleDateChange('end', e.target.value)
                        }
                    />

                    <button
                        onClick={handleRefresh}
                        className="p-2 hover:bg-gray-100 rounded-md transition-colors text-gray-500"
                        title="Refresh"
                    >
                        <RefreshCw
                            className={`h-4 w-4 ${
                                loading ? 'animate-spin' : ''
                            }`}
                        />
                    </button>

                    <div className="w-px h-6 bg-gray-200"></div>

                    <button
                        onClick={exportCSV}
                        disabled={
                            loading ||
                            Object.keys(metrics).length === 0
                        }
                        className="p-2 hover:bg-gray-100 rounded-md transition-colors text-gray-500 disabled:opacity-30 disabled:cursor-not-allowed"
                        title="Export as CSV"
                    >
                        <Download className="h-4 w-4" />
                    </button>

                    <div className="relative group/bi">
                        <button
                            className="p-2 rounded-md transition-colors text-gray-300 cursor-not-allowed"
                            disabled
                            title="BI Integration - Coming Soon"
                        >
                            <DatabaseZap className="h-4 w-4" />
                        </button>

                        <div className="absolute top-full right-0 mt-2 px-3 py-1.5 bg-gray-900 text-white text-[11px] rounded-md opacity-0 group-hover/bi:opacity-100 transition-opacity whitespace-nowrap pointer-events-none z-10">
                            BI Integration — Coming Soon
                        </div>
                    </div>
                </div>
            </div>

            {loading ? (
                <DashboardLoader />
            ) : (
                <>
                    {errorMessage && (
                        <div className="bg-red-50 border border-red-200 text-red-700 rounded-lg px-4 py-3 text-sm flex items-center justify-between gap-4">
                            <span>{errorMessage}</span>

                            <button
                                onClick={handleRefresh}
                                className="shrink-0 px-3 py-1.5 text-sm font-medium text-red-700 bg-red-100 hover:bg-red-200 rounded-md transition-colors"
                            >
                                Retry
                            </button>
                        </div>
                    )}

                    {/* KPI Cards */}
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <KPICard
                            label="Open Rate"
                            value={openRate}
                            subtext="Opens / Delivered"
                            color="blue"
                            trend="up"
                        />

                        <KPICard
                            label="Click Through Rate"
                            value={clickRate}
                            subtext="Clicks / Delivered"
                            color="purple"
                            trend="up"
                        />

                        <KPICard
                            label="Bounce Rate"
                            value={bounceRate}
                            subtext="Bounces / Delivered"
                            color="orange"
                            trend="down"
                        />
                    </div>

                    {/* Charts Section */}
                    <div className="grid grid-cols-1 gap-6">
                        {/* Funnel Chart */}
                        <div className="bg-white p-6 rounded-xl border border-gray-100 shadow-sm">
                            <h3 className="text-lg font-semibold text-gray-900 mb-6">
                                Engagement Funnel
                            </h3>

                            <div className="h-96">
                                <ResponsiveContainer
                                    width="100%"
                                    height="100%"
                                >
                                    <BarChart
                                        data={funnelData}
                                        layout="vertical"
                                        margin={{
                                            top: 5,
                                            right: 30,
                                            left: 20,
                                            bottom: 5
                                        }}
                                    >
                                        <CartesianGrid
                                            strokeDasharray="3 3"
                                            horizontal={false}
                                        />

                                        <XAxis
                                            type="number"
                                            hide
                                        />

                                        <YAxis
                                            dataKey="name"
                                            type="category"
                                            width={80}
                                            tick={{ fontSize: 12 }}
                                        />

                                        <RechartsTooltip
                                            cursor={{ fill: 'transparent' }}
                                        />

                                        <Bar
                                            dataKey="value"
                                            fill="#ff7226"
                                            radius={[0, 4, 4, 0]}
                                            barSize={40}
                                        />
                                    </BarChart>
                                </ResponsiveContainer>
                            </div>
                        </div>
                    </div>

                    {/* Detailed Metrics Grid */}
                    <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
                        <div className="px-6 py-4 border-b border-gray-200 bg-gray-50/50">
                            <h3 className="text-base font-semibold text-gray-900">
                                Detailed Metrics
                            </h3>
                        </div>

                        {Object.entries(metrics).length === 0 ? (
                            <div className="px-6 py-8 text-sm text-gray-500">
                                No metrics available for the selected period.
                            </div>
                        ) : (
                            <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 divide-x divide-y divide-gray-100">
                                {Object.entries(metrics).map(
                                    ([key, value]) => {
                                        const def =
                                            METRIC_DEFINITIONS[key] ||
                                            METRIC_DEFINITIONS.default;

                                        return (
                                            <div
                                                key={key}
                                                onClick={() =>
                                                    navigate(
                                                        `/email/${key}?start=${dates.start}&end=${dates.end}`
                                                    )
                                                }
                                                className="p-4 hover:bg-gray-50 transition-colors group relative cursor-pointer"
                                            >
                                                <div className="flex items-center gap-1 mb-1">
                                                    <span className="text-xs font-medium text-orange-600 hover: uppercase tracking-wide">
                                                        {def.label}
                                                    </span>

                                                    <div className="group/tooltip relative">
                                                        <Info className="h-3 w-3 text-gray-300 cursor-help" />

                                                        <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 px-2 py-1 bg-gray-900 text-white text-[10px] rounded opacity-0 group-hover/tooltip:opacity-100 transition-opacity w-32 text-center pointer-events-none z-10">
                                                            {def.desc}
                                                        </div>
                                                    </div>
                                                </div>

                                                <div className="text-lg font-semibold text-gray-900">
                                                    {value.toLocaleString()}
                                                </div>
                                            </div>
                                        );
                                    }
                                )}
                            </div>
                        )}
                    </div>
                </>
            )}
        </div>
    );
}